import type { CSSProperties } from 'react'
import styled from 'styled-components'

export const GenerationToolbar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;
`

/* Keeps the "Billing month" label glued to its picker as one control group. */
export const ToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

export const ToolbarDivider = styled.div`
  width: 1px;
  align-self: stretch;
  margin: 2px 2px;
  background: #e5e7eb;
`

export const ToolbarLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7280;
  white-space: nowrap;
`

export const ToolbarSpacer = styled.div`
  flex: 1;
`

export const GenerateAllButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid #008a91;
  background: #00a5ad;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #008a91;
    border-color: #008a91;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`

export const ResultsCount = styled.span`
  font-size: 13px;
  color: #6b7280;
  white-space: nowrap;
`

export const PeriodCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
`

/* ---- Drawer header ---- */

export const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

export const DrawerHeaderTitle = styled.div`
  font-weight: 700;
  color: #111827;
`

export const DrawerHeaderMeta = styled.div`
  font-size: 12px;
  color: #6b7280;
`

export const DrawerLoading = styled.div`
  padding: 28px 0;
  text-align: center;
`

/* AntD Drawer body style (its own prop, not a DOM style attribute). */
export const drawerBodyStyle: CSSProperties = {
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
}

/* ---- Plan Cost cell (invoice over BER) ---- */

export const PlanCostCell = styled.div`
  line-height: 1.3;
  text-align: right;
`

export const PlanCostInvoice = styled.div<{ $credit?: boolean }>`
  color: ${({ $credit }) => ($credit ? '#16a34a' : 'inherit')};
`

export const PlanCostBer = styled.div`
  font-size: 11px;
  color: #9ca3af;
`

/* ---- Benefit ID (click to copy) ---- */

export const BenefitIdButton = styled.button`
  padding: 0;
  border: none;
  background: transparent;
  color: #374151;
  font: inherit;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid #00a5ad;
    outline-offset: 2px;
    border-radius: 2px;
  }
`

export const VersionsText = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid #9cd9dc;
  border-radius: 999px;
  background: #d4edee;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 600;
  color: #008a91;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: #5fc2c6;
    background: #c2e8ea;
  }

  .anticon {
    font-size: 9px;
    transition: transform 0.15s;
  }

  &[data-expanded='true'] .anticon {
    transform: rotate(180deg);
  }
`

export const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
  background: #fef3c7;
  color: #92400e;
  white-space: nowrap;

  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #d97706;
  }
`

export const RegenerateButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  line-height: 1.5;
  transition:
    background 0.15s,
    border-color 0.15s;
  border: 1px solid #9cd9dc;
  background: #d4edee;
  color: #008a91;

  &:hover:not(:disabled) {
    border-color: #5fc2c6;
    background: #c2e8ea;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const ExpandedContainer = styled.div`
  padding: 12px 18px;
  background: #f8fafc;
  border-top: 1px solid #f1f5f9;
`

export const ExpandedTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 10px;
`

export const ExpandedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const ExpandedRow = styled.button`
  width: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  padding: 10px 12px;
  display: grid;
  grid-template-columns: 70px 1fr auto auto;
  gap: 12px;
  align-items: center;
  text-align: left;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: #9cd9dc;
    background: #f2fbfb;
  }
`

export const VersionBadge = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #008a91;
  background: #d4edee;
  border: 1px solid #9cd9dc;
  border-radius: 6px;
  padding: 2px 8px;
  width: fit-content;
`

export const ExpandedMeta = styled.div`
  font-size: 12px;
  color: #64748b;
`

export const ExpandedAmount = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #111827;
`

export const EmptyText = styled.div`
  font-size: 12px;
  color: #6b7280;
`

/* ---- Invoice Summary (mirrors the employer funding statement mockup) ---- */

export const SummaryWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 640px;
`

export const SummaryLines = styled.div`
  overflow: hidden;
`

export const SummaryRow = styled.div<{ $credit?: boolean }>`
  display: grid;
  grid-template-columns: 1fr auto auto auto;
  align-items: center;
  padding: 13px 24px;
  gap: 0 20px;

  & + & {
    border-top: 1px solid rgba(0, 0, 0, 0.045);
  }

  .summary-label {
    font-size: 14px;
    color: ${({ $credit }) => ($credit ? '#c85c5c' : '#7b8794')};
    display: flex;
    flex-direction: column;
    gap: 2px;

    small {
      font-size: 12px;
      color: #9aa5b1;
    }
  }

  .summary-rate {
    font-size: 12px;
    color: #7b8794;
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  .summary-sym {
    width: 12px;
    text-align: right;
    font-size: 14px;
    color: ${({ $credit }) => ($credit ? '#c85c5c' : '#7b8794')};
  }

  .summary-val {
    min-width: 96px;
    text-align: right;
    font-size: 14px;
    font-weight: 600;
    color: ${({ $credit }) => ($credit ? '#c85c5c' : '#1f2933')};
    font-variant-numeric: tabular-nums;
  }
`

/* Groups related summary rows (e.g. fee lines) under a small heading. */
export const SummaryGroupLabel = styled.div`
  padding: 14px 24px 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #9aa5b1;
  border-top: 1px solid rgba(0, 0, 0, 0.045);
`

export const SummaryNote = styled.p`
  margin: 0;
  padding: 8px 24px 0;
  font-size: 12px;
  color: #9aa5b1;
`

export const SummaryTotalCard = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto auto;
  align-items: center;
  padding: 16px 24px;
  gap: 0 20px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.07);

  .summary-total-label {
    font-size: 16px;
    font-weight: 700;
    color: #1f2933;
  }

  .summary-total-sym {
    width: 12px;
    text-align: right;
    font-size: 16px;
    font-weight: 700;
    color: #1f2933;
  }

  .summary-total-val {
    min-width: 96px;
    text-align: right;
    font-size: 20px;
    font-weight: 700;
    color: #1f2933;
    font-variant-numeric: tabular-nums;
  }
`

export const DrawerTabEmptyState = styled.div`
  padding: 24px;
  text-align: center;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  color: #64748b;
  font-size: 13px;
`

export const DrawerTabsContainer = styled.div`
  height: 100%;
  display: flex;
  flex: 1;
  min-height: 0;
  /* min-width:0 stops the flex children from expanding to the table's content
     width (a wide table would otherwise blow out the layout and clip instead of
     scrolling). */
  min-width: 0;

  .ant-tabs {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
    min-width: 0;
  }

  .ant-tabs-content-holder {
    flex: 1;
    min-height: 0;
    min-width: 0;
    overflow: hidden;
  }

  .ant-tabs-content {
    height: 100%;
    min-width: 0;
  }

  .ant-tabs-tabpane {
    height: 100%;
    min-width: 0;
    overflow: hidden;
  }
`

/*
 * Pane for the ag-grid backed drawer tabs. The grid needs a sized container to
 * lay itself out, so fill the tab pane and let the grid own its own scrolling.
 */
export const DrawerGridPane = styled.div`
  height: 100%;
  width: 100%;
  min-height: 0;
  min-width: 0;

  --ag-font-size: 12px;
  --ag-header-foreground-color: #6b7280;
  --ag-active-color: #00a5ad;
  --ag-selected-row-background-color: #f2fbfb;
  --ag-row-hover-color: #f8fafc;

  /*
   * Plan Cost stacks the invoice amount over the BER amount. ag-grid centres
   * single-line cells with line-height, which leaves stacked content pinned to
   * the top of the cell, so lay this one out as a flex box instead.
   */
  .ag-cell.plan-cost-cell {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    line-height: 1.3;
  }
`

export const DrawerTablePane = styled.div`
  height: 100%;
  min-height: 0;
  min-width: 0;

  .ant-table-wrapper,
  .ant-spin-nested-loading,
  .ant-spin-container,
  .ant-table,
  .ant-table-container {
    height: 100%;
    min-height: 0;
    min-width: 0;
  }

  /*
   * Make the table a full-height flex column so the body grows to fill the pane
   * (AntD's scroll.y only sets max-height, which would leave a gap on short row
   * sets). min-width:0 keeps the body at the pane width so a wide table scrolls
   * horizontally inside the body instead of expanding the layout.
   */
  .ant-table-container {
    display: flex;
    flex-direction: column;
  }

  .ant-table-header {
    flex: 0 0 auto;
    min-width: 0;
  }

  .ant-table-body {
    flex: 1 1 auto;
    min-height: 0;
    min-width: 0;
    max-height: none !important;
    overflow-x: auto !important;
    overflow-y: auto !important;
  }
`
